export type Lang = "en" | "es" | "fr" | "ar" | "ur" | "pt" | "de" | "zh" | "tr" | "sw" | "it" | "ru";

export const langNames: Record<Lang, string> = {
  en: "English",
  ar: "Arabic",
  ur: "Urdu",
  fr: "French",
  es: "Spanish",
  pt: "Portuguese",
  tr: "Turkish",
  sw: "Swahili",
  de: "German",
  it: "Italian",
  ru: "Russian",
  zh: "Chinese",
};

export const langFlags: Record<Lang, string> = {
  en: "🇬🇧",
  ar: "🇸🇦",
  ur: "🇵🇰",
  fr: "🇫🇷",
  es: "🇪🇸",
  pt: "🇵🇹",
  tr: "🇹🇷",
  sw: "🇰🇪",
  de: "🇩🇪",
  it: "🇮🇹",
  ru: "🇷🇺",
  zh: "🇨🇳",
};

export const rtlLangs: Lang[] = ["ar", "ur"];

const en: Record<string, string> = {
  "nav.features": "Features",
  "nav.pricing": "Pricing",
  "nav.blog": "Blog",
  "nav.login": "Login",
  "nav.cta": "Start Free Trial",
  "hero.badge": "AI-Powered Chatbot",
  "hero.title1": "Your Business,",
  "hero.title2": "Always Answering",
  "hero.desc": "AI chatbot trained on",
  "hero.desc.highlight": "your actual website",
  "hero.desc.end": "— not generic responses. Never miss a customer again.",
  "hero.placeholder": "Paste your website URL...",
  "hero.cta": "Get Started Free",
  "hero.note": "No credit card required · Setup in 2 minutes",
  "stats.businesses": "Businesses",
  "stats.conversations": "Conversations",
  "stats.languages": "Languages",
  "how.title": "How It Works",
  "how.subtitle": "Get your AI receptionist running in three simple steps.",
  "how.step1.title": "Paste your URL",
  "how.step1.desc": "Firecrawl learns your business — services, FAQs, hours, everything.",
  "how.step2.title": "Customize your widget",
  "how.step2.desc": "Match your brand colors, set a name, and personalize your AI agent.",
  "how.step3.title": "Go live in minutes",
  "how.step3.desc": "Embed one line of code. Your AI receptionist is ready to answer.",
  "features.title": "Powerful Features",
  "features.subtitle": "Everything you need to automate customer interactions.",
  "features.chat.title": "24/7 AI Chat",
  "features.chat.desc": "Always-on chat that handles inquiries, FAQs, and leads while you sleep.",
  "features.booking.title": "Appointment Booking",
  "features.booking.desc": "Let customers book directly in the chat. Syncs with your calendar.",
  "features.crawl.title": "Auto-learns from Website",
  "features.crawl.desc": "Firecrawl scans your site and trains the AI on your business instantly.",
  "features.routing.title": "Smart Page Routing",
  "features.routing.desc": "Directs customers to the right page, product, or service automatically.",
  "features.lang.title": "20+ Languages",
  "features.lang.desc": "Serve international customers in their native language, effortlessly.",
  "features.rag.title": "Real Knowledge. Real Answers.",
  "features.rag.desc": "Noorify crawls your entire website and trains your AI on your real content. Every answer comes from your actual business data — not hallucinated generic AI responses. Competitors charge $49/mo for this. You get it from $5.",
  "compare.title": "Why Noorify Beats Generic Chatbots",
  "compare.subtitle": "Real business-trained AI, booking, and RAG training — starting free and growing from just $5/mo.",
  "compare.feature": "Feature",
  "compare.generic": "Generic Chatbot",
  "compare.chatgpt": "ChatGPT Plugins",
  "compare.bestValue": "Best Value",
  "compare.value": "Value",
  "compare.generic.val": "Generic answers",
  "compare.chatgpt.val": "Extra setup + cost",
  "compare.noorify.val": "Starts free · from $5/mo",
  "compare.cta": "Get business-trained AI without expensive monthly tools",
  "compare.btn": "Start Free Now",
  "compare.f1": "Trained on your website",
  "compare.f2": "Auto-crawls your pages",
  "compare.f3": "Learns from documents (RAG)",
  "compare.f4": "Real business answers",
  "compare.f5": "Appointment booking built-in",
  "compare.f6": "Custom branding",
  "pricing.title": "Simple Pricing",
  "pricing.subtitle": "Start free, upgrade when you're ready. The most affordable AI chatbot — period.",
  "pricing.cta": "Get Started",
  "pricing.quote": '"Most businesses spend $200-500/month on missed calls. Noorify pays for itself with one saved customer."',
  "pricing.compare": "See how Noorify compares to competitors →",
  "faq.title": "Frequently Asked Questions",
  "footer.copy": "© 2025 Noorify.pro",
};

const es: Record<string, string> = {
  "nav.features": "Funciones",
  "nav.pricing": "Precios",
  "nav.blog": "Blog",
  "nav.login": "Iniciar sesión",
  "nav.cta": "Prueba Gratis",
  "hero.badge": "Chatbot con IA",
  "hero.title1": "Tu Negocio,",
  "hero.title2": "Siempre Respondiendo",
  "hero.desc": "Chatbot IA entrenado con",
  "hero.desc.highlight": "tu sitio web real",
  "hero.desc.end": "— sin respuestas genéricas. No pierdas más clientes.",
  "hero.placeholder": "Pega la URL de tu sitio web...",
  "hero.cta": "Comienza Gratis",
  "hero.note": "Sin tarjeta de crédito · En 2 minutos",
  "stats.businesses": "Negocios",
  "stats.conversations": "Conversaciones",
  "stats.languages": "Idiomas",
  "how.title": "Cómo Funciona",
  "how.subtitle": "Pon en marcha tu recepcionista IA en tres simples pasos.",
  "how.step1.title": "Pega tu URL",
  "how.step1.desc": "Firecrawl aprende tu negocio — servicios, FAQs, horarios, todo.",
  "how.step2.title": "Personaliza tu widget",
  "how.step2.desc": "Ajusta los colores, nombre y la voz de tu agente IA.",
  "how.step3.title": "Lanza en minutos",
  "how.step3.desc": "Inserta una línea de código. Tu recepcionista IA está listo.",
  "features.title": "Funciones Potentes",
  "features.subtitle": "Todo lo que necesitas para automatizar la atención al cliente.",
  "features.chat.title": "Chat IA 24/7",
  "features.chat.desc": "Chat siempre activo que maneja consultas, FAQs y leads mientras duermes.",
  "features.booking.title": "Reserva de Citas",
  "features.booking.desc": "Permite a los clientes reservar directamente en el chat.",
  "features.crawl.title": "Aprende de tu Web",
  "features.crawl.desc": "Escanea tu sitio y entrena la IA con tu negocio al instante.",
  "features.routing.title": "Enrutamiento Inteligente",
  "features.routing.desc": "Dirige a los clientes a la página correcta automáticamente.",
  "features.lang.title": "20+ Idiomas",
  "features.lang.desc": "Atiende clientes internacionales en su idioma nativo.",
  "features.rag.title": "Conocimiento Real. Respuestas Reales.",
  "features.rag.desc": "Noorify rastrea tu sitio web completo y entrena tu IA con tu contenido real. Competidores cobran $49/mes. Tú lo tienes desde $5.",
  "compare.title": "Por Qué Noorify Supera a los Chatbots Genéricos",
  "compare.subtitle": "IA entrenada en tu negocio real, reservas y RAG — desde gratis y desde $5/mes.",
  "compare.feature": "Función",
  "compare.generic": "Chatbot Genérico",
  "compare.chatgpt": "Plugins ChatGPT",
  "compare.bestValue": "Mejor Valor",
  "compare.value": "Valor",
  "compare.generic.val": "Respuestas genéricas",
  "compare.chatgpt.val": "Configuración extra + costo",
  "compare.noorify.val": "Gratis · desde $5/mes",
  "compare.cta": "Obtén IA entrenada sin herramientas mensuales costosas",
  "compare.btn": "Comienza Gratis",
  "compare.f1": "Entrenado en tu web",
  "compare.f2": "Rastrea tus páginas automáticamente",
  "compare.f3": "Aprende de documentos (RAG)",
  "compare.f4": "Respuestas reales de negocio",
  "compare.f5": "Reserva de citas integrada",
  "compare.f6": "Marca personalizada",
  "pricing.title": "Precios Simples",
  "pricing.subtitle": "Comienza gratis, actualiza cuando estés listo. El chatbot IA más económico.",
  "pricing.cta": "Comenzar",
  "pricing.quote": '"La mayoría de negocios gastan $200-500/mes en llamadas perdidas. Noorify se paga solo con un cliente recuperado."',
  "pricing.compare": "Compara Noorify con la competencia →",
  "faq.title": "Preguntas Frecuentes",
  "footer.copy": "© 2025 Noorify.pro",
};

const fr: Record<string, string> = {
  "nav.features": "Fonctionnalités",
  "nav.pricing": "Tarifs",
  "nav.blog": "Blog",
  "nav.login": "Connexion",
  "nav.cta": "Essai Gratuit",
  "hero.badge": "Chatbot IA",
  "hero.title1": "Votre Entreprise,",
  "hero.title2": "Toujours Disponible",
  "hero.desc": "Chatbot IA formé sur",
  "hero.desc.highlight": "votre vrai site web",
  "hero.desc.end": "— pas de réponses génériques. Ne ratez plus aucun client.",
  "hero.placeholder": "Collez l'URL de votre site...",
  "hero.cta": "Commencer Gratuitement",
  "hero.note": "Sans carte bancaire · En 2 minutes",
  "stats.businesses": "Entreprises",
  "stats.conversations": "Conversations",
  "stats.languages": "Langues",
  "how.title": "Comment Ça Marche",
  "how.subtitle": "Lancez votre réceptionniste IA en trois étapes simples.",
  "how.step1.title": "Collez votre URL",
  "how.step1.desc": "Firecrawl analyse votre entreprise — services, FAQ, horaires, tout.",
  "how.step2.title": "Personnalisez votre widget",
  "how.step2.desc": "Adaptez les couleurs, le nom et la voix de votre agent IA.",
  "how.step3.title": "En ligne en minutes",
  "how.step3.desc": "Intégrez une ligne de code. Votre réceptionniste IA est prêt.",
  "features.title": "Fonctionnalités Puissantes",
  "features.subtitle": "Tout ce qu'il faut pour automatiser les interactions clients.",
  "features.chat.title": "Chat IA 24/7",
  "features.chat.desc": "Chat permanent qui gère demandes, FAQ et prospects pendant votre sommeil.",
  "features.booking.title": "Prise de Rendez-vous",
  "features.booking.desc": "Les clients réservent directement dans le chat.",
  "features.crawl.title": "Apprend de votre Site",
  "features.crawl.desc": "Scanne votre site et forme l'IA sur votre entreprise instantanément.",
  "features.routing.title": "Routage Intelligent",
  "features.routing.desc": "Dirige les clients vers la bonne page automatiquement.",
  "features.lang.title": "20+ Langues",
  "features.lang.desc": "Servez les clients internationaux dans leur langue maternelle.",
  "features.rag.title": "Vrai Savoir. Vraies Réponses.",
  "features.rag.desc": "Noorify parcourt votre site entier et forme votre IA sur votre vrai contenu. Les concurrents facturent 49$/mois. Vous l'obtenez dès 5$.",
  "compare.title": "Pourquoi Noorify Bat les Chatbots Génériques",
  "compare.subtitle": "IA formée sur votre entreprise, réservations et RAG — gratuit et à partir de 5$/mois.",
  "compare.feature": "Fonctionnalité",
  "compare.generic": "Chatbot Générique",
  "compare.chatgpt": "Plugins ChatGPT",
  "compare.bestValue": "Meilleur Rapport",
  "compare.value": "Valeur",
  "compare.generic.val": "Réponses génériques",
  "compare.chatgpt.val": "Config. supplémentaire + coût",
  "compare.noorify.val": "Gratuit · dès 5$/mois",
  "compare.cta": "Obtenez une IA formée sans outils mensuels coûteux",
  "compare.btn": "Commencer Gratuitement",
  "compare.f1": "Formé sur votre site web",
  "compare.f2": "Explore vos pages automatiquement",
  "compare.f3": "Apprend des documents (RAG)",
  "compare.f4": "Vraies réponses professionnelles",
  "compare.f5": "Réservation intégrée",
  "compare.f6": "Personnalisation de marque",
  "pricing.title": "Tarifs Simples",
  "pricing.subtitle": "Commencez gratuitement, évoluez quand vous êtes prêt.",
  "pricing.cta": "Commencer",
  "pricing.quote": '"La plupart des entreprises dépensent 200-500$/mois en appels manqués. Noorify se rentabilise avec un seul client sauvé."',
  "pricing.compare": "Comparez Noorify à la concurrence →",
  "faq.title": "Questions Fréquentes",
  "footer.copy": "© 2025 Noorify.pro",
};

const ar: Record<string, string> = {
  "nav.features": "المميزات",
  "nav.pricing": "الأسعار",
  "nav.blog": "المدونة",
  "nav.login": "تسجيل الدخول",
  "nav.cta": "ابدأ مجاناً",
  "hero.badge": "شات بوت بالذكاء الاصطناعي",
  "hero.title1": "عملك،",
  "hero.title2": "يجيب دائماً",
  "hero.desc": "شات بوت مدرب على",
  "hero.desc.highlight": "موقعك الفعلي",
  "hero.desc.end": "— ليس ردود عامة. لا تفقد أي عميل بعد الآن.",
  "hero.placeholder": "الصق رابط موقعك...",
  "hero.cta": "ابدأ مجاناً",
  "hero.note": "بدون بطاقة ائتمان · جاهز في دقيقتين",
  "stats.businesses": "أعمال",
  "stats.conversations": "محادثات",
  "stats.languages": "لغات",
  "how.title": "كيف يعمل",
  "how.subtitle": "شغّل مساعدك الذكي في ثلاث خطوات بسيطة.",
  "how.step1.title": "الصق رابطك",
  "how.step1.desc": "Firecrawl يتعلم عن عملك — الخدمات، الأسئلة، المواعيد، كل شيء.",
  "how.step2.title": "خصص الودجت",
  "how.step2.desc": "طابق ألوان علامتك التجارية واختر صوت مساعدك الذكي.",
  "how.step3.title": "ابدأ في دقائق",
  "how.step3.desc": "أضف سطر كود واحد. مساعدك الذكي جاهز للإجابة.",
  "features.title": "مميزات قوية",
  "features.subtitle": "كل ما تحتاجه لأتمتة تفاعلات العملاء.",
  "features.chat.title": "دردشة ذكية 24/7",
  "features.chat.desc": "دردشة دائمة تتعامل مع الاستفسارات والعملاء المحتملين أثناء نومك.",
  "features.booking.title": "حجز المواعيد",
  "features.booking.desc": "اسمح للعملاء بالحجز مباشرة في الدردشة.",
  "features.crawl.title": "يتعلم من موقعك تلقائياً",
  "features.crawl.desc": "يفحص موقعك ويدرب الذكاء الاصطناعي على عملك فوراً.",
  "features.routing.title": "توجيه ذكي للصفحات",
  "features.routing.desc": "يوجه العملاء للصفحة الصحيحة تلقائياً.",
  "features.lang.title": "أكثر من 20 لغة",
  "features.lang.desc": "اخدم العملاء الدوليين بلغتهم الأم.",
  "features.rag.title": "معرفة حقيقية. إجابات حقيقية.",
  "features.rag.desc": "Noorify يزحف على موقعك بالكامل ويدرب ذكاءك الاصطناعي على محتواك الحقيقي. المنافسون يتقاضون 49$/شهر. أنت تحصل عليه من 5$.",
  "compare.title": "لماذا Noorify يتفوق على الشات بوتات العادية",
  "compare.subtitle": "ذكاء اصطناعي مدرب على عملك الحقيقي — مجاني ومن 5$/شهر فقط.",
  "compare.feature": "الميزة",
  "compare.generic": "شات بوت عادي",
  "compare.chatgpt": "إضافات ChatGPT",
  "compare.bestValue": "أفضل قيمة",
  "compare.value": "القيمة",
  "compare.generic.val": "ردود عامة",
  "compare.chatgpt.val": "إعداد إضافي + تكلفة",
  "compare.noorify.val": "مجاني · من 5$/شهر",
  "compare.cta": "احصل على ذكاء اصطناعي مدرب بدون أدوات مكلفة",
  "compare.btn": "ابدأ مجاناً الآن",
  "compare.f1": "مدرب على موقعك",
  "compare.f2": "يزحف على صفحاتك تلقائياً",
  "compare.f3": "يتعلم من المستندات (RAG)",
  "compare.f4": "ردود أعمال حقيقية",
  "compare.f5": "حجز مواعيد مدمج",
  "compare.f6": "علامة تجارية مخصصة",
  "pricing.title": "أسعار بسيطة",
  "pricing.subtitle": "ابدأ مجاناً، حدّث عندما تكون جاهزاً. أرخص شات بوت بالذكاء الاصطناعي.",
  "pricing.cta": "ابدأ",
  "pricing.quote": '"معظم الشركات تنفق 200-500$/شهر على المكالمات الفائتة. Noorify يدفع تكلفته من عميل واحد محفوظ."',
  "pricing.compare": "قارن Noorify مع المنافسين →",
  "faq.title": "الأسئلة الشائعة",
  "footer.copy": "© 2025 Noorify.pro",
};

const ur: Record<string, string> = {
  "nav.features": "خصوصیات",
  "nav.pricing": "قیمتیں",
  "nav.blog": "بلاگ",
  "nav.login": "لاگ ان",
  "nav.cta": "مفت شروع کریں",
  "hero.badge": "AI چیٹ بوٹ",
  "hero.title1": "آپ کا کاروبار،",
  "hero.title2": "ہمیشہ جواب دیتا",
  "hero.desc": "AI چیٹ بوٹ جو تربیت یافتہ ہے",
  "hero.desc.highlight": "آپ کی اصل ویب سائٹ",
  "hero.desc.end": "پر — عام جوابات نہیں۔ کوئی گاہک نہ کھوئیں۔",
  "hero.placeholder": "اپنی ویب سائٹ کا URL پیسٹ کریں...",
  "hero.cta": "مفت شروع کریں",
  "hero.note": "کریڈٹ کارڈ کی ضرورت نہیں · 2 منٹ میں سیٹ اپ",
  "stats.businesses": "کاروبار",
  "stats.conversations": "بات چیت",
  "stats.languages": "زبانیں",
  "how.title": "یہ کیسے کام کرتا ہے",
  "how.subtitle": "اپنا AI ریسپشنسٹ تین آسان مراحل میں شروع کریں۔",
  "how.step1.title": "اپنا URL پیسٹ کریں",
  "how.step1.desc": "Firecrawl آپ کا کاروبار سیکھتا ہے — خدمات، سوالات، اوقات، سب کچھ۔",
  "how.step2.title": "اپنا ویجیٹ حسب ضرورت بنائیں",
  "how.step2.desc": "اپنے برانڈ کے رنگ، نام اور AI ایجنٹ کی آواز منتخب کریں۔",
  "how.step3.title": "منٹوں میں لائیو ہوں",
  "how.step3.desc": "ایک لائن کوڈ شامل کریں۔ آپ کا AI ریسپشنسٹ تیار ہے۔",
  "features.title": "طاقتور خصوصیات",
  "features.subtitle": "گاہکوں کے تعامل کو خودکار بنانے کے لیے سب کچھ۔",
  "features.chat.title": "24/7 AI چیٹ",
  "features.chat.desc": "ہمیشہ فعال چیٹ جو سوالات، FAQs اور لیڈز کو سنبھالتا ہے۔",
  "features.booking.title": "اپائنٹمنٹ بکنگ",
  "features.booking.desc": "گاہکوں کو چیٹ میں براہ راست بکنگ کرنے دیں۔",
  "features.crawl.title": "ویب سائٹ سے خود سیکھتا ہے",
  "features.crawl.desc": "آپ کی سائٹ اسکین کرتا ہے اور AI کو فوری طور پر تربیت دیتا ہے۔",
  "features.routing.title": "سمارٹ صفحہ روٹنگ",
  "features.routing.desc": "گاہکوں کو خودکار طور پر صحیح صفحے پر لے جاتا ہے۔",
  "features.lang.title": "20+ زبانیں",
  "features.lang.desc": "بین الاقوامی گاہکوں کو ان کی مادری زبان میں خدمت فراہم کریں۔",
  "features.rag.title": "حقیقی علم۔ حقیقی جوابات۔",
  "features.rag.desc": "Noorify آپ کی پوری ویب سائٹ کو کرال کرتا ہے اور آپ کے حقیقی مواد پر AI کو تربیت دیتا ہے۔ حریف $49/ماہ لیتے ہیں۔ آپ کو $5 سے ملتا ہے۔",
  "compare.title": "Noorify عام چیٹ بوٹس سے بہتر کیوں ہے",
  "compare.subtitle": "آپ کے کاروبار پر تربیت یافتہ AI — مفت شروع اور صرف $5/ماہ سے۔",
  "compare.feature": "خصوصیت",
  "compare.generic": "عام چیٹ بوٹ",
  "compare.chatgpt": "ChatGPT پلگ ان",
  "compare.bestValue": "بہترین قیمت",
  "compare.value": "قیمت",
  "compare.generic.val": "عام جوابات",
  "compare.chatgpt.val": "اضافی سیٹ اپ + لاگت",
  "compare.noorify.val": "مفت · $5/ماہ سے",
  "compare.cta": "مہنگے ماہانہ ٹولز کے بغیر تربیت یافتہ AI حاصل کریں",
  "compare.btn": "ابھی مفت شروع کریں",
  "compare.f1": "آپ کی ویب سائٹ پر تربیت یافتہ",
  "compare.f2": "آپ کے صفحات خود بخود کرال کرتا ہے",
  "compare.f3": "دستاویزات سے سیکھتا ہے (RAG)",
  "compare.f4": "حقیقی کاروباری جوابات",
  "compare.f5": "بلٹ ان اپائنٹمنٹ بکنگ",
  "compare.f6": "حسب ضرورت برانڈنگ",
  "pricing.title": "سادہ قیمتیں",
  "pricing.subtitle": "مفت شروع کریں، جب تیار ہوں اپ گریڈ کریں۔",
  "pricing.cta": "شروع کریں",
  "pricing.quote": '"زیادہ تر کاروبار $200-500/ماہ چھوٹی ہوئی کالوں پر خرچ کرتے ہیں۔ Noorify ایک بچائے گئے گاہک سے خود کو ادا کرتا ہے۔"',
  "pricing.compare": "Noorify کا حریفوں سے موازنہ کریں →",
  "faq.title": "اکثر پوچھے گئے سوالات",
  "footer.copy": "© 2025 Noorify.pro",
};

const pt: Record<string, string> = {
  "nav.features": "Recursos",
  "nav.pricing": "Preços",
  "nav.blog": "Blog",
  "nav.login": "Entrar",
  "nav.cta": "Teste Grátis",
  "hero.badge": "Chatbot com IA",
  "hero.title1": "Seu Negócio,",
  "hero.title2": "Sempre Respondendo",
  "hero.desc": "Chatbot IA treinado no",
  "hero.desc.highlight": "seu site real",
  "hero.desc.end": "— sem respostas genéricas. Nunca perca um cliente.",
  "hero.placeholder": "Cole a URL do seu site...",
  "hero.cta": "Comece Grátis",
  "hero.note": "Sem cartão de crédito · Em 2 minutos",
  "stats.businesses": "Negócios",
  "stats.conversations": "Conversas",
  "stats.languages": "Idiomas",
  "how.title": "Como Funciona",
  "how.subtitle": "Lance seu recepcionista IA em três passos simples.",
  "how.step1.title": "Cole sua URL",
  "how.step1.desc": "Firecrawl aprende seu negócio — serviços, FAQ, horários, tudo.",
  "how.step2.title": "Personalize seu widget",
  "how.step2.desc": "Ajuste as cores, nome e voz do seu agente IA.",
  "how.step3.title": "Online em minutos",
  "how.step3.desc": "Insira uma linha de código. Seu recepcionista IA está pronto.",
  "features.title": "Recursos Poderosos",
  "features.subtitle": "Tudo para automatizar o atendimento ao cliente.",
  "features.chat.title": "Chat IA 24/7",
  "features.chat.desc": "Chat sempre ativo que lida com consultas e leads enquanto você dorme.",
  "features.booking.title": "Agendamento",
  "features.booking.desc": "Clientes agendam diretamente no chat.",
  "features.crawl.title": "Aprende do seu Site",
  "features.crawl.desc": "Escaneia seu site e treina a IA no seu negócio instantaneamente.",
  "features.routing.title": "Roteamento Inteligente",
  "features.routing.desc": "Direciona clientes para a página certa automaticamente.",
  "features.lang.title": "20+ Idiomas",
  "features.lang.desc": "Atenda clientes internacionais no idioma nativo.",
  "features.rag.title": "Conhecimento Real. Respostas Reais.",
  "features.rag.desc": "Noorify rastreia todo seu site e treina sua IA com seu conteúdo real. Concorrentes cobram $49/mês. Você obtém a partir de $5.",
  "compare.title": "Por Que Noorify Supera Chatbots Genéricos",
  "compare.subtitle": "IA treinada no seu negócio — grátis e a partir de $5/mês.",
  "compare.feature": "Recurso",
  "compare.generic": "Chatbot Genérico",
  "compare.chatgpt": "Plugins ChatGPT",
  "compare.bestValue": "Melhor Valor",
  "compare.value": "Valor",
  "compare.generic.val": "Respostas genéricas",
  "compare.chatgpt.val": "Config. extra + custo",
  "compare.noorify.val": "Grátis · a partir de $5/mês",
  "compare.cta": "Obtenha IA treinada sem ferramentas mensais caras",
  "compare.btn": "Comece Grátis",
  "compare.f1": "Treinado no seu site",
  "compare.f2": "Rastreia suas páginas automaticamente",
  "compare.f3": "Aprende de documentos (RAG)",
  "compare.f4": "Respostas reais de negócio",
  "compare.f5": "Agendamento integrado",
  "compare.f6": "Marca personalizada",
  "pricing.title": "Preços Simples",
  "pricing.subtitle": "Comece grátis, atualize quando estiver pronto.",
  "pricing.cta": "Começar",
  "pricing.quote": '"A maioria dos negócios gasta $200-500/mês em chamadas perdidas. Noorify se paga com um cliente salvo."',
  "pricing.compare": "Compare Noorify com concorrentes →",
  "faq.title": "Perguntas Frequentes",
  "footer.copy": "© 2025 Noorify.pro",
};

const de: Record<string, string> = {
  "nav.features": "Funktionen",
  "nav.pricing": "Preise",
  "nav.blog": "Blog",
  "nav.login": "Anmelden",
  "nav.cta": "Kostenlos testen",
  "hero.badge": "KI-Chatbot",
  "hero.title1": "Ihr Geschäft,",
  "hero.title2": "Immer Erreichbar",
  "hero.desc": "KI-Chatbot trainiert auf",
  "hero.desc.highlight": "Ihrer echten Website",
  "hero.desc.end": "— keine generischen Antworten. Verpassen Sie nie wieder einen Kunden.",
  "hero.placeholder": "Website-URL einfügen...",
  "hero.cta": "Kostenlos starten",
  "hero.note": "Keine Kreditkarte · In 2 Minuten eingerichtet",
  "stats.businesses": "Unternehmen",
  "stats.conversations": "Gespräche",
  "stats.languages": "Sprachen",
  "how.title": "So funktioniert's",
  "how.subtitle": "Starten Sie Ihren KI-Rezeptionisten in drei einfachen Schritten.",
  "how.step1.title": "URL einfügen",
  "how.step1.desc": "Firecrawl lernt Ihr Unternehmen — Services, FAQs, Öffnungszeiten, alles.",
  "how.step2.title": "Widget anpassen",
  "how.step2.desc": "Passen Sie Farben, Namen und Stimme Ihres KI-Agenten an.",
  "how.step3.title": "In Minuten live",
  "how.step3.desc": "Eine Zeile Code einfügen. Ihr KI-Rezeptionist ist bereit.",
  "features.title": "Leistungsstarke Funktionen",
  "features.subtitle": "Alles für die Automatisierung der Kundeninteraktion.",
  "features.chat.title": "24/7 KI-Chat",
  "features.chat.desc": "Immer aktiver Chat für Anfragen, FAQs und Leads — auch nachts.",
  "features.booking.title": "Terminbuchung",
  "features.booking.desc": "Kunden buchen direkt im Chat.",
  "features.crawl.title": "Lernt von Ihrer Website",
  "features.crawl.desc": "Scannt Ihre Website und trainiert die KI sofort.",
  "features.routing.title": "Intelligentes Routing",
  "features.routing.desc": "Leitet Kunden automatisch zur richtigen Seite.",
  "features.lang.title": "20+ Sprachen",
  "features.lang.desc": "Bedienen Sie internationale Kunden in ihrer Muttersprache.",
  "features.rag.title": "Echtes Wissen. Echte Antworten.",
  "features.rag.desc": "Noorify crawlt Ihre gesamte Website und trainiert Ihre KI mit echten Inhalten. Wettbewerber verlangen 49$/Monat. Sie erhalten es ab 5$.",
  "compare.title": "Warum Noorify generische Chatbots schlägt",
  "compare.subtitle": "Auf Ihr Unternehmen trainierte KI — kostenlos und ab 5$/Monat.",
  "compare.feature": "Funktion",
  "compare.generic": "Generischer Chatbot",
  "compare.chatgpt": "ChatGPT Plugins",
  "compare.bestValue": "Bestes Angebot",
  "compare.value": "Wert",
  "compare.generic.val": "Generische Antworten",
  "compare.chatgpt.val": "Extra Setup + Kosten",
  "compare.noorify.val": "Kostenlos · ab 5$/Monat",
  "compare.cta": "Trainierte KI ohne teure monatliche Tools",
  "compare.btn": "Jetzt kostenlos starten",
  "compare.f1": "Auf Ihrer Website trainiert",
  "compare.f2": "Crawlt Ihre Seiten automatisch",
  "compare.f3": "Lernt aus Dokumenten (RAG)",
  "compare.f4": "Echte Geschäftsantworten",
  "compare.f5": "Integrierte Terminbuchung",
  "compare.f6": "Individuelles Branding",
  "pricing.title": "Einfache Preise",
  "pricing.subtitle": "Kostenlos starten, upgraden wenn Sie bereit sind.",
  "pricing.cta": "Loslegen",
  "pricing.quote": '"Die meisten Unternehmen geben 200-500$/Monat für verpasste Anrufe aus. Noorify zahlt sich mit einem geretteten Kunden."',
  "pricing.compare": "Noorify mit Wettbewerbern vergleichen →",
  "faq.title": "Häufig gestellte Fragen",
  "footer.copy": "© 2025 Noorify.pro",
};

const tr: Record<string, string> = {
  "nav.features": "Özellikler",
  "nav.pricing": "Fiyatlar",
  "nav.blog": "Blog",
  "nav.login": "Giriş",
  "nav.cta": "Ücretsiz Başla",
  "hero.badge": "Yapay Zeka Chatbot",
  "hero.title1": "İşletmeniz,",
  "hero.title2": "Her Zaman Cevap Veriyor",
  "hero.desc": "Yapay zeka chatbotu eğitildi",
  "hero.desc.highlight": "gerçek web siteniz",
  "hero.desc.end": "üzerinde — genel yanıtlar değil. Hiçbir müşteriyi kaçırmayın.",
  "hero.placeholder": "Web sitenizin URL'sini yapıştırın...",
  "hero.cta": "Ücretsiz Başla",
  "hero.note": "Kredi kartı gerekmez · 2 dakikada kurulum",
  "stats.businesses": "İşletme",
  "stats.conversations": "Konuşma",
  "stats.languages": "Dil",
  "how.title": "Nasıl Çalışır",
  "how.subtitle": "AI resepsiyonistinizi üç basit adımda başlatın.",
  "how.step1.title": "URL'nizi yapıştırın",
  "how.step1.desc": "Firecrawl işletmenizi öğrenir — hizmetler, SSS, saatler, her şey.",
  "how.step2.title": "Widget'ınızı özelleştirin",
  "how.step2.desc": "Marka renklerinizi eşleştirin ve AI ajanınızın sesini seçin.",
  "how.step3.title": "Dakikalar içinde canlı",
  "how.step3.desc": "Bir satır kod ekleyin. AI resepsiyonistiniz hazır.",
  "features.title": "Güçlü Özellikler",
  "features.subtitle": "Müşteri etkileşimlerini otomatikleştirmek için her şey.",
  "features.chat.title": "7/24 AI Sohbet",
  "features.chat.desc": "Siz uyurken soruları, SSS'leri ve potansiyel müşterileri yöneten her zaman aktif sohbet.",
  "features.booking.title": "Randevu Alma",
  "features.booking.desc": "Müşterilerin doğrudan sohbette randevu almasını sağlayın.",
  "features.crawl.title": "Web Sitesinden Otomatik Öğrenir",
  "features.crawl.desc": "Sitenizi tarar ve AI'yi anında eğitir.",
  "features.routing.title": "Akıllı Yönlendirme",
  "features.routing.desc": "Müşterileri otomatik olarak doğru sayfaya yönlendirir.",
  "features.lang.title": "20+ Dil",
  "features.lang.desc": "Uluslararası müşterilere ana dillerinde hizmet verin.",
  "features.rag.title": "Gerçek Bilgi. Gerçek Cevaplar.",
  "features.rag.desc": "Noorify tüm sitenizi tarar ve AI'nızı gerçek içeriğinizle eğitir. Rakipler 49$/ay alıyor. Siz 5$'dan alıyorsunuz.",
  "compare.title": "Noorify Neden Genel Chatbotları Yener",
  "compare.subtitle": "İşletmenizde eğitilmiş AI — ücretsiz ve 5$/aydan başlayan.",
  "compare.feature": "Özellik",
  "compare.generic": "Genel Chatbot",
  "compare.chatgpt": "ChatGPT Eklentileri",
  "compare.bestValue": "En İyi Değer",
  "compare.value": "Değer",
  "compare.generic.val": "Genel yanıtlar",
  "compare.chatgpt.val": "Ekstra kurulum + maliyet",
  "compare.noorify.val": "Ücretsiz · 5$/aydan",
  "compare.cta": "Pahalı aylık araçlar olmadan eğitilmiş AI edinin",
  "compare.btn": "Şimdi Ücretsiz Başla",
  "compare.f1": "Web sitenizde eğitildi",
  "compare.f2": "Sayfalarınızı otomatik tarar",
  "compare.f3": "Belgelerden öğrenir (RAG)",
  "compare.f4": "Gerçek iş yanıtları",
  "compare.f5": "Yerleşik randevu alma",
  "compare.f6": "Özel markalama",
  "pricing.title": "Basit Fiyatlandırma",
  "pricing.subtitle": "Ücretsiz başlayın, hazır olduğunuzda yükseltin.",
  "pricing.cta": "Başla",
  "pricing.quote": '"Çoğu işletme kaçırılan aramalar için 200-500$/ay harcar. Noorify bir kurtarılan müşteriyle kendini amorti eder."',
  "pricing.compare": "Noorify'ı rakiplerle karşılaştırın →",
  "faq.title": "Sıkça Sorulan Sorular",
  "footer.copy": "© 2025 Noorify.pro",
};

const zh: Record<string, string> = {
  "nav.features": "功能",
  "nav.pricing": "定价",
  "nav.blog": "博客",
  "nav.login": "登录",
  "nav.cta": "免费试用",
  "hero.badge": "AI 聊天机器人",
  "hero.title1": "您的业务，",
  "hero.title2": "永远在线回复",
  "hero.desc": "AI 聊天机器人基于",
  "hero.desc.highlight": "您的真实网站",
  "hero.desc.end": "训练 — 不是通用回复。不再错过任何客户。",
  "hero.placeholder": "粘贴您的网站 URL...",
  "hero.cta": "免费开始",
  "hero.note": "无需信用卡 · 2分钟设置",
  "stats.businesses": "企业",
  "stats.conversations": "对话",
  "stats.languages": "语言",
  "how.title": "工作原理",
  "how.subtitle": "三个简单步骤启动您的 AI 接待员。",
  "how.step1.title": "粘贴您的 URL",
  "how.step1.desc": "Firecrawl 学习您的业务 — 服务、常见问题、营业时间，一切。",
  "how.step2.title": "自定义小部件",
  "how.step2.desc": "匹配品牌颜色，设置名称，选择 AI 代理的声音。",
  "how.step3.title": "几分钟上线",
  "how.step3.desc": "嵌入一行代码。您的 AI 接待员已准备就绪。",
  "features.title": "强大功能",
  "features.subtitle": "自动化客户互动所需的一切。",
  "features.chat.title": "24/7 AI 聊天",
  "features.chat.desc": "始终在线的聊天，处理咨询、常见问题和潜在客户。",
  "features.booking.title": "预约",
  "features.booking.desc": "让客户直接在聊天中预约。",
  "features.crawl.title": "自动从网站学习",
  "features.crawl.desc": "扫描您的网站并即时训练 AI。",
  "features.routing.title": "智能页面路由",
  "features.routing.desc": "自动将客户引导至正确的页面。",
  "features.lang.title": "20+ 种语言",
  "features.lang.desc": "用母语为国际客户提供服务。",
  "features.rag.title": "真实知识。真实回答。",
  "features.rag.desc": "Noorify 爬取您的整个网站，用真实内容训练 AI。竞争对手收费 $49/月。您只需 $5 起。",
  "compare.title": "Noorify 为何胜过通用聊天机器人",
  "compare.subtitle": "基于您业务训练的 AI — 免费开始，仅 $5/月起。",
  "compare.feature": "功能",
  "compare.generic": "通用聊天机器人",
  "compare.chatgpt": "ChatGPT 插件",
  "compare.bestValue": "最佳价值",
  "compare.value": "价值",
  "compare.generic.val": "通用回复",
  "compare.chatgpt.val": "额外设置 + 费用",
  "compare.noorify.val": "免费 · $5/月起",
  "compare.cta": "无需昂贵工具即可获得训练过的 AI",
  "compare.btn": "立即免费开始",
  "compare.f1": "基于您的网站训练",
  "compare.f2": "自动爬取您的页面",
  "compare.f3": "从文档学习 (RAG)",
  "compare.f4": "真实业务回答",
  "compare.f5": "内置预约",
  "compare.f6": "自定义品牌",
  "pricing.title": "简单定价",
  "pricing.subtitle": "免费开始，准备好了再升级。",
  "pricing.cta": "开始",
  "pricing.quote": '"大多数企业每月在错过的电话上花费 $200-500。Noorify 只需挽回一个客户即可回本。"',
  "pricing.compare": "比较 Noorify 与竞争对手 →",
  "faq.title": "常见问题",
  "footer.copy": "© 2025 Noorify.pro",
};

const sw: Record<string, string> = {
  "nav.features": "Vipengele",
  "nav.pricing": "Bei",
  "nav.blog": "Blogu",
  "nav.login": "Ingia",
  "nav.cta": "Anza Bure",
  "hero.badge": "Chatbot ya AI",
  "hero.title1": "Biashara Yako,",
  "hero.title2": "Daima Inajibu",
  "hero.desc": "Chatbot ya AI iliyofunzwa kwenye",
  "hero.desc.highlight": "tovuti yako halisi",
  "hero.desc.end": "— si majibu ya kawaida. Usikose mteja tena.",
  "hero.placeholder": "Bandika URL ya tovuti yako...",
  "hero.cta": "Anza Bure",
  "hero.note": "Hakuna kadi ya mkopo · Usanidi wa dakika 2",
  "stats.businesses": "Biashara",
  "stats.conversations": "Mazungumzo",
  "stats.languages": "Lugha",
  "how.title": "Jinsi Inavyofanya Kazi",
  "how.subtitle": "Anzisha mpokezi wako wa AI kwa hatua tatu rahisi.",
  "how.step1.title": "Bandika URL yako",
  "how.step1.desc": "Firecrawl inajifunza biashara yako — huduma, maswali, masaa, kila kitu.",
  "how.step2.title": "Binafsisha wijeti yako",
  "how.step2.desc": "Linganisha rangi za chapa yako na uchague sauti ya wakala wako wa AI.",
  "how.step3.title": "Anza moja kwa moja kwa dakika",
  "how.step3.desc": "Weka mstari mmoja wa msimbo. Mpokezi wako wa AI yuko tayari.",
  "features.title": "Vipengele Vyenye Nguvu",
  "features.subtitle": "Kila kitu unachohitaji kubinafsisha mwingiliano wa wateja.",
  "features.chat.title": "Mazungumzo ya AI 24/7",
  "features.chat.desc": "Mazungumzo ya kila wakati yanayoshughulikia maswali na wateja wanaowezekana.",
  "features.booking.title": "Kupanga Miadi",
  "features.booking.desc": "Waache wateja wapange moja kwa moja kwenye mazungumzo.",
  "features.crawl.title": "Inajifunza Kutoka Tovuti",
  "features.crawl.desc": "Inascan tovuti yako na kufunza AI mara moja.",
  "features.routing.title": "Uelekezaji wa Akili",
  "features.routing.desc": "Inawaelekeza wateja kwenye ukurasa sahihi kiotomatiki.",
  "features.lang.title": "Lugha 20+",
  "features.lang.desc": "Hudumia wateja wa kimataifa kwa lugha yao ya asili.",
  "features.rag.title": "Ujuzi Halisi. Majibu Halisi.",
  "features.rag.desc": "Noorify inacrawl tovuti yako yote na kufunza AI yako kwa maudhui yako halisi. Washindani wanashtaki $49/mwezi. Unapata kuanzia $5.",
  "compare.title": "Kwa Nini Noorify Inashinda Chatbot za Kawaida",
  "compare.subtitle": "AI iliyofunzwa kwenye biashara yako — bure na kuanzia $5/mwezi.",
  "compare.feature": "Kipengele",
  "compare.generic": "Chatbot ya Kawaida",
  "compare.chatgpt": "ChatGPT Plugins",
  "compare.bestValue": "Thamani Bora",
  "compare.value": "Thamani",
  "compare.generic.val": "Majibu ya kawaida",
  "compare.chatgpt.val": "Usanidi wa ziada + gharama",
  "compare.noorify.val": "Bure · kuanzia $5/mwezi",
  "compare.cta": "Pata AI iliyofunzwa bila zana ghali za kila mwezi",
  "compare.btn": "Anza Bure Sasa",
  "compare.f1": "Imefunzwa kwenye tovuti yako",
  "compare.f2": "Inacrawl kurasa zako kiotomatiki",
  "compare.f3": "Inajifunza kutoka hati (RAG)",
  "compare.f4": "Majibu halisi ya biashara",
  "compare.f5": "Upangaji wa miadi uliojumuishwa",
  "compare.f6": "Chapa ya kibinafsi",
  "pricing.title": "Bei Rahisi",
  "pricing.subtitle": "Anza bure, panda daraja ukiwa tayari.",
  "pricing.cta": "Anza",
  "pricing.quote": '"Biashara nyingi zinatumia $200-500/mwezi kwa simu zilizokosekana. Noorify inalipa yenyewe kwa mteja mmoja aliyeokolewa."',
  "pricing.compare": "Linganisha Noorify na washindani →",
  "faq.title": "Maswali Yanayoulizwa Mara kwa Mara",
  "footer.copy": "© 2025 Noorify.pro",
};

const it: Record<string, string> = {
  "nav.features": "Funzionalità",
  "nav.pricing": "Prezzi",
  "nav.blog": "Blog",
  "nav.login": "Accedi",
  "nav.cta": "Prova Gratuita",
  "hero.badge": "Chatbot IA",
  "hero.title1": "La Tua Attività,",
  "hero.title2": "Sempre in Risposta",
  "hero.desc": "Chatbot IA addestrato sul",
  "hero.desc.highlight": "tuo sito web reale",
  "hero.desc.end": "— non risposte generiche. Non perdere mai più un cliente.",
  "hero.placeholder": "Incolla l'URL del tuo sito...",
  "hero.cta": "Inizia Gratis",
  "hero.note": "Nessuna carta di credito · Setup in 2 minuti",
  "stats.businesses": "Aziende",
  "stats.conversations": "Conversazioni",
  "stats.languages": "Lingue",
  "how.title": "Come Funziona",
  "how.subtitle": "Avvia il tuo receptionist IA in tre semplici passi.",
  "how.step1.title": "Incolla il tuo URL",
  "how.step1.desc": "Firecrawl impara la tua attività — servizi, FAQ, orari, tutto.",
  "how.step2.title": "Personalizza il widget",
  "how.step2.desc": "Abbina i colori del tuo brand e scegli la voce del tuo agente IA.",
  "how.step3.title": "Online in minuti",
  "how.step3.desc": "Inserisci una riga di codice. Il tuo receptionist IA è pronto.",
  "features.title": "Funzionalità Potenti",
  "features.subtitle": "Tutto il necessario per automatizzare le interazioni con i clienti.",
  "features.chat.title": "Chat IA 24/7",
  "features.chat.desc": "Chat sempre attiva che gestisce richieste, FAQ e lead mentre dormi.",
  "features.booking.title": "Prenotazione Appuntamenti",
  "features.booking.desc": "I clienti prenotano direttamente nella chat.",
  "features.crawl.title": "Impara dal tuo Sito",
  "features.crawl.desc": "Scansiona il tuo sito e addestra l'IA istantaneamente.",
  "features.routing.title": "Instradamento Intelligente",
  "features.routing.desc": "Indirizza automaticamente i clienti alla pagina giusta.",
  "features.lang.title": "20+ Lingue",
  "features.lang.desc": "Servi clienti internazionali nella loro lingua madre.",
  "features.rag.title": "Conoscenza Reale. Risposte Reali.",
  "features.rag.desc": "Noorify scansiona tutto il tuo sito e addestra la tua IA con i tuoi contenuti reali. I concorrenti chiedono $49/mese. Tu lo ottieni da $5.",
  "compare.title": "Perché Noorify Batte i Chatbot Generici",
  "compare.subtitle": "IA addestrata sulla tua attività — gratis e da $5/mese.",
  "compare.feature": "Funzionalità",
  "compare.generic": "Chatbot Generico",
  "compare.chatgpt": "Plugin ChatGPT",
  "compare.bestValue": "Miglior Valore",
  "compare.value": "Valore",
  "compare.generic.val": "Risposte generiche",
  "compare.chatgpt.val": "Setup extra + costo",
  "compare.noorify.val": "Gratis · da $5/mese",
  "compare.cta": "Ottieni IA addestrata senza costosi strumenti mensili",
  "compare.btn": "Inizia Gratis Ora",
  "compare.f1": "Addestrato sul tuo sito",
  "compare.f2": "Scansiona le tue pagine automaticamente",
  "compare.f3": "Impara dai documenti (RAG)",
  "compare.f4": "Risposte aziendali reali",
  "compare.f5": "Prenotazione integrata",
  "compare.f6": "Branding personalizzato",
  "pricing.title": "Prezzi Semplici",
  "pricing.subtitle": "Inizia gratis, aggiorna quando sei pronto.",
  "pricing.cta": "Inizia",
  "pricing.quote": '"La maggior parte delle aziende spende $200-500/mese per chiamate perse. Noorify si ripaga con un solo cliente salvato."',
  "pricing.compare": "Confronta Noorify con i concorrenti →",
  "faq.title": "Domande Frequenti",
  "footer.copy": "© 2025 Noorify.pro",
};

const ru: Record<string, string> = {
  "nav.features": "Функции",
  "nav.pricing": "Цены",
  "nav.blog": "Блог",
  "nav.login": "Войти",
  "nav.cta": "Бесплатная пробная",
  "hero.badge": "ИИ-чатбот",
  "hero.title1": "Ваш Бизнес,",
  "hero.title2": "Всегда Отвечает",
  "hero.desc": "ИИ-чатбот обучен на",
  "hero.desc.highlight": "вашем реальном сайте",
  "hero.desc.end": "— не шаблонные ответы. Больше ни одного упущенного клиента.",
  "hero.placeholder": "Вставьте URL вашего сайта...",
  "hero.cta": "Начать бесплатно",
  "hero.note": "Без кредитной карты · Настройка за 2 минуты",
  "stats.businesses": "Бизнесов",
  "stats.conversations": "Разговоров",
  "stats.languages": "Языков",
  "how.title": "Как это работает",
  "how.subtitle": "Запустите своего ИИ-рецепциониста за три простых шага.",
  "how.step1.title": "Вставьте URL",
  "how.step1.desc": "Firecrawl изучает ваш бизнес — услуги, FAQ, часы работы, всё.",
  "how.step2.title": "Настройте виджет",
  "how.step2.desc": "Подберите цвета бренда, имя и голос ИИ-агента.",
  "how.step3.title": "Запуск за минуты",
  "how.step3.desc": "Вставьте одну строку кода. Ваш ИИ-рецепционист готов.",
  "features.title": "Мощные Функции",
  "features.subtitle": "Всё для автоматизации взаимодействия с клиентами.",
  "features.chat.title": "ИИ-чат 24/7",
  "features.chat.desc": "Постоянный чат для запросов, FAQ и лидов — даже ночью.",
  "features.booking.title": "Запись на приём",
  "features.booking.desc": "Клиенты записываются прямо в чате.",
  "features.crawl.title": "Обучается с сайта",
  "features.crawl.desc": "Сканирует сайт и мгновенно обучает ИИ.",
  "features.routing.title": "Умная маршрутизация",
  "features.routing.desc": "Автоматически направляет клиентов на нужную страницу.",
  "features.lang.title": "20+ языков",
  "features.lang.desc": "Обслуживайте клиентов на их родном языке.",
  "features.rag.title": "Реальные знания. Реальные ответы.",
  "features.rag.desc": "Noorify сканирует весь сайт и обучает ИИ на вашем контенте. Конкуренты берут $49/мес. Вы получаете от $5.",
  "compare.title": "Почему Noorify лучше обычных чатботов",
  "compare.subtitle": "ИИ обученный на вашем бизнесе — бесплатно и от $5/мес.",
  "compare.feature": "Функция",
  "compare.generic": "Обычный чатбот",
  "compare.chatgpt": "Плагины ChatGPT",
  "compare.bestValue": "Лучшая цена",
  "compare.value": "Ценность",
  "compare.generic.val": "Шаблонные ответы",
  "compare.chatgpt.val": "Доп. настройка + расходы",
  "compare.noorify.val": "Бесплатно · от $5/мес",
  "compare.cta": "Получите обученный ИИ без дорогих инструментов",
  "compare.btn": "Начать бесплатно",
  "compare.f1": "Обучен на вашем сайте",
  "compare.f2": "Автоматически сканирует страницы",
  "compare.f3": "Учится из документов (RAG)",
  "compare.f4": "Реальные бизнес-ответы",
  "compare.f5": "Встроенная запись на приём",
  "compare.f6": "Индивидуальный брендинг",
  "pricing.title": "Простые цены",
  "pricing.subtitle": "Начните бесплатно, обновите когда будете готовы.",
  "pricing.cta": "Начать",
  "pricing.quote": '"Большинство бизнесов тратят $200-500/мес на пропущенные звонки. Noorify окупается одним спасённым клиентом."',
  "pricing.compare": "Сравните Noorify с конкурентами →",
  "faq.title": "Часто задаваемые вопросы",
  "footer.copy": "© 2025 Noorify.pro",
};

export const translations: Record<Lang, Record<string, string>> = {
  en, es, fr, ar, ur, pt, de, zh, tr, sw, it, ru,
};
